<template>
    <div class="page-404-bg">
        <div class="wrapper-pages pt-2 pt-sm-5 pb-4 pb-sm-5 position-relative">
            <div class="container">
                <div class="row justify-content-center">
                    <div class="col-xxl-4 col-lg-5">
                        <div class="brand-logo mb-5 text-center">
                            <a href="/">
                                <router-link to="/login">
                                    <img :src="`${$logoDir}logo-auth.png`" alt="HAXO" height="72" class="img-fluid">
                                </router-link>
                            </a>
                        </div>

                        <div class="card">
                            <div class="card-body p-4">
                                <div class="text-center">
                                    <h1 class="text-error">{{ actionData.title }}</h1>
                                    <p class="text-white mt-3">{{ actionData.description }}</p>
                                </div>

                                <form v-if="!isCompleted" @submit.prevent="handleSubmit" class="pb-4 mt-4">
                                    <div class="d-grid mb-0 text-center">
                                        <button class="btn" :class="actionData.buttonClass || 'btn-info'" type="submit"
                                            :disabled="isLoading">
                                            <i class="mdi mdi-check"></i> {{ actionData.buttonText }}
                                        </button>
                                    </div>
                                    <div v-if="errorMessage" class="mt-3 text-danger text-center">
                                        {{ errorMessage }}
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <footer class="footer footer-alt text-white">
            {{ year }} {{ $t('common.copyright') }}
        </footer>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import axios from '@/services/axios';

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const year = new Date().getFullYear();

const action = route.query.action;
const uuid = route.query.uuid;
const installationName = route.query.name;

const errorMessage = ref(null);
const isLoading = ref(false);
const isCompleted = ref(false);

const actionsConfig = computed(() => ({
    accept_replace_request_current: {
        title: t('confirmAction.actions.accept_replace_request_current.title'),
        description: t('confirmAction.actions.accept_replace_request_current.description', { name: installationName }),
        buttonText: t('confirmAction.actions.accept_replace_request_current.buttonText'),
        buttonClass: "btn-info",
        successMessage: t('confirmAction.actions.accept_replace_request_current.successMessage'),
        apiCall: (uuid) => axios.post(`/conf/facilities/accept/${uuid}`)
    },
    decline_replace_request_current: {
        title: t('confirmAction.actions.decline_replace_request_current.title'),
        description: t('confirmAction.actions.decline_replace_request_current.description', { name: installationName }),
        buttonText: t('confirmAction.actions.decline_replace_request_current.buttonText'),
        buttonClass: "btn-warning",
        successMessage: t('confirmAction.actions.decline_replace_request_current.successMessage'),
        apiCall: (uuid) => axios.post(`/conf/facilities/decline/${uuid}`)
    },
    accept_replace_request_new: {
        title: t('confirmAction.actions.accept_replace_request_new.title'),
        description: t('confirmAction.actions.accept_replace_request_new.description', { name: installationName }),
        buttonText: t('confirmAction.actions.accept_replace_request_new.buttonText'),
        buttonClass: "btn-info",
        successMessage: t('confirmAction.actions.accept_replace_request_new.successMessage'),
        apiCall: (uuid) => axios.post(`/conf/facilities/accept/${uuid}`)
    },
    decline_replace_request_new: {
        title: t('confirmAction.actions.decline_replace_request_new.title'),
        description: t('confirmAction.actions.decline_replace_request_new.description', { name: installationName }),
        buttonText: t('confirmAction.actions.decline_replace_request_new.buttonText'),
        buttonClass: "btn-warning",
        successMessage: t('confirmAction.actions.decline_replace_request_new.successMessage'),
        apiCall: (uuid) => axios.post(`/conf/facilities/decline/${uuid}`)
    },
    confirm_delete_installer: {
        title: t('confirmAction.actions.confirm_delete_installer.title'),
        description: t('confirmAction.actions.confirm_delete_installer.description'),
        buttonText: t('confirmAction.actions.confirm_delete_installer.buttonText'),
        buttonClass: "btn-danger",
        successMessage: t('confirmAction.actions.confirm_delete_installer.successMessage'),
        apiCall: (uuid) => axios.post(`/conf/installers/confirm_delete/${uuid}`)
    },
    cancel_delete_installer: {
        title: t('confirmAction.actions.cancel_delete_installer.title'),
        description: t('confirmAction.actions.cancel_delete_installer.description'),
        buttonText: t('confirmAction.actions.cancel_delete_installer.buttonText'),
        buttonClass: "btn-warning",
        successMessage: t('confirmAction.actions.cancel_delete_installer.successMessage'),
        apiCall: (uuid) => axios.post(`/conf/installers/cancel_delete/${uuid}`)
    },
    accept_installer: {
        title: t('confirmAction.actions.accept_installer.title'),
        description: t('confirmAction.actions.accept_installer.description'),
        buttonText: t('confirmAction.actions.accept_installer.buttonText'),
        buttonClass: "btn-info",
        successMessage: t('confirmAction.actions.accept_installer.successMessage'),
        apiCall: (uuid) => axios.post(`/conf/installers/accept/${uuid}`)
    },
    accept_user: {
        title: t('confirmAction.actions.accept_user.title'),
        description: t('confirmAction.actions.accept_user.description'),
        buttonText: t('confirmAction.actions.accept_user.buttonText'),
        buttonClass: "btn-info",
        apiCall: (uuid) => axios.post(`/conf/users/accept/${uuid}`)
    },
    // decline member invite
    decline_invitation: {
        title: t('confirmAction.actions.decline_invitation.title'),
        description: t('confirmAction.actions.decline_invitation.description'),
        buttonText: t('confirmAction.actions.decline_invitation.buttonText'),
        buttonClass: "btn-danger",
        successMessage: t('confirmAction.actions.decline_invitation.successMessage'),
        apiCall: (uuid) => axios.post(`/conf/invitations/${uuid}/decline`)
    },
    // accept / decline a new installation on the administrator account (query param `name` = installation name)
    accept_installation: {
        title: t('confirmAction.actions.accept_installation.title'),
        description: t('confirmAction.actions.accept_installation.description', { name: installationName }),
        buttonText: t('confirmAction.actions.accept_installation.buttonText'),
        buttonClass: "btn-info",
        successMessage: t('confirmAction.actions.accept_installation.successMessage'),
        apiCall: (uuid) => axios.post(`/conf/facilities/validations/${uuid}/accept`)
    },
    decline_installation: {
        title: t('confirmAction.actions.decline_installation.title'),
        description: t('confirmAction.actions.decline_installation.description', { name: installationName }),
        buttonText: t('confirmAction.actions.decline_installation.buttonText'),
        buttonClass: "btn-danger",
        successMessage: t('confirmAction.actions.decline_installation.successMessage'),
        apiCall: (uuid) => axios.post(`/conf/facilities/validations/${uuid}/decline`)
    },
}));

const actionData = computed(() => {
    if (!action || !actionsConfig.value[action]) {
        return {
            title: t('confirmAction.defaultTitle'),
            description: t('confirmAction.loading'),
            buttonText: t('confirmAction.defaultButton'),
            buttonClass: 'btn-info'
        };
    }
    return actionsConfig.value[action];
});

onMounted(() => {
    if (!action || !actionsConfig.value[action]) {
        router.push({ path: "/404" });
    }
});

async function handleSubmit() {
    errorMessage.value = null;
    isLoading.value = true;
    try {
        await actionData.value.apiCall(uuid);
        isCompleted.value = true;
        router.push({ path: "/result", query: { success: true, title: t('common.success'), message: actionData.value.successMessage } });
    } catch (error) {
        errorMessage.value = error.response?.status === 404
            ? t('confirmAction.errors.notFound')
            : t('confirmAction.errors.generic');
    } finally {
        isLoading.value = false;
    }
}
</script>

<style scoped>
.page-404-bg {
    background: rgb(45, 64, 140);
    background: linear-gradient(164deg, rgb(32, 123, 194) 0%, rgb(45, 64, 140) 50%, rgb(114, 44, 132) 100%);
}

.page-404-bg .wrapper-pages {
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    min-height: 100vh;
}

.page-404-bg .card {
    background: rgba(255, 255, 255, 0.11);
    border-radius: 16px;
    -webkit-box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(5px);
    -webkit-backdrop-filter: blur(5px);
    border: 1px solid rgba(255, 255, 255, 0.3);
}

.page-404-bg .text-error {
    color: #fff !important;
    text-shadow: rgba(255, 255, 255, 0.3) 5px 1px, rgba(255, 255, 255, 0.2) 10px 3px;
    font-size: 32px;
}
</style>
